/**
 * Hub Page: Shoulder Pain Treatment in Hyderabad
 * SEO-optimized page targeting "shoulder pain treatment hyderabad", "shoulder specialist hyderabad"
 * Symptom-based grid linking to specific condition pages
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CTASection } from '../components/CTASection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Moon, ArrowUp, Zap, RotateCcw, Activity, Bone, Phone, MapPin, Clock } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export const ShoulderPainTreatmentHyderabad = () => {
  const { theme, doctor } = useSite();

  // Symptom-based navigation - what patients search for
  const symptomCategories = [
    {
      icon: <Moon className="w-8 h-8" />,
      symptom: "Shoulder Pain at Night",
      description: "Pain that wakes you up or prevents sleep",
      conditions: [
        { name: "Rotator Cuff Tear", link: "/rotator-cuff-tear" },
        { name: "Frozen Shoulder", link: "/frozen-shoulder" },
        { name: "Shoulder Impingement", link: "/shoulder-impingement" }
      ]
    },
    {
      icon: <ArrowUp className="w-8 h-8" />,
      symptom: "Pain Lifting Arm Overhead",
      description: "Difficulty reaching up or lifting objects",
      conditions: [
        { name: "Shoulder Impingement", link: "/shoulder-impingement" },
        { name: "Rotator Cuff Tear", link: "/rotator-cuff-tear" },
        { name: "Calcific Tendonitis", link: "/calcific-tendonitis" }
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      symptom: "Sudden Severe Pain",
      description: "Acute pain after injury or spontaneous onset",
      conditions: [
        { name: "Shoulder Dislocation", link: "/shoulder-dislocation" },
        { name: "Calcific Tendonitis", link: "/calcific-tendonitis" },
        { name: "AC Joint Injury", link: "/ac-joint-injury" }
      ]
    },
    {
      icon: <RotateCcw className="w-8 h-8" />,
      symptom: "Clicking or Instability",
      description: "Shoulder feels loose, clicks, or slips out",
      conditions: [
        { name: "Labral Tears", link: "/labral-tears" },
        { name: "SLAP Tears", link: "/slap-tears" },
        { name: "MDI (Multidirectional Instability)", link: "/mdi" }
      ]
    },
    {
      icon: <Activity className="w-8 h-8" />,
      symptom: "Stiffness & Restricted Movement",
      description: "Cannot move shoulder normally, getting stuck",
      conditions: [
        { name: "Frozen Shoulder", link: "/frozen-shoulder" },
        { name: "Shoulder Arthritis", link: "/shoulder-arthritis" },
        { name: "Post-Injury Stiffness", link: "/shoulder-pain" }
      ]
    },
    {
      icon: <Bone className="w-8 h-8" />,
      symptom: "Pain After Fall/Trauma",
      description: "Shoulder pain following an accident or fall",
      conditions: [
        { name: "Clavicle Fracture", link: "/clavicle-fracture" },
        { name: "Shoulder Fracture", link: "/shoulder-fracture" },
        { name: "Shoulder Dislocation", link: "/shoulder-dislocation" }
      ]
    }
  ];

  // All condition pages for comprehensive linking
  const allConditions = [
    { name: "Rotator Cuff Tear", link: "/rotator-cuff-tear", priority: "high" },
    { name: "Frozen Shoulder", link: "/frozen-shoulder", priority: "high" },
    { name: "Shoulder Dislocation", link: "/shoulder-dislocation", priority: "high" },
    { name: "Shoulder Arthritis", link: "/shoulder-arthritis", priority: "high" },
    { name: "Shoulder Replacement", link: "/shoulder-replacement", priority: "high" },
    { name: "Labral Tears", link: "/labral-tears", priority: "high" },
    { name: "Shoulder Impingement", link: "/shoulder-impingement", priority: "medium" },
    { name: "Calcific Tendonitis", link: "/calcific-tendonitis", priority: "medium" },
    { name: "Biceps Tendon Injury", link: "/biceps-tendon-injury", priority: "medium" },
    { name: "SLAP Tears", link: "/slap-tears", priority: "medium" },
    { name: "AC Joint Injury", link: "/ac-joint-injury", priority: "medium" },
    { name: "Clavicle Fracture", link: "/clavicle-fracture", priority: "medium" },
    { name: "Shoulder Fracture", link: "/shoulder-fracture", priority: "medium" },
    { name: "Scapula Fracture", link: "/scapula-fracture", priority: "low" },
    { name: "SC Joint Injury", link: "/sc-joint-injury", priority: "low" },
    { name: "MDI", link: "/mdi", priority: "low" },
    { name: "Reverse Shoulder Replacement", link: "/reverse-shoulder-replacement", priority: "low" },
    { name: "Arthroscopic Surgery", link: "/arthroscopic-shoulder-surgery", priority: "medium" }
  ];

  return (
    <div className="min-h-screen" data-testid="shoulder-pain-hyderabad-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Shoulder Pain Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Expert diagnosis and treatment by Dr. Deepthi Nandan Reddy at Apollo Hospitals Financial District. 
              UK & Mayo Clinic trained. Non-surgical options first.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer">
                <button 
                  className="px-8 py-4 rounded-lg text-white font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: theme?.primaryHex }}
                >
                  <Phone className="w-5 h-5" />
                  Book Consultation
                </button>
              </a>
              <a href={`tel:${doctor.phone}`}>
                <button className="px-8 py-4 rounded-lg border-2 font-medium flex items-center gap-2 hover:bg-gray-50 transition-colors" style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}>
                  Call {doctor.phone}
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Introduction */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <img 
                src={doctor.doctorImage} 
                alt={doctor.name}
                className="w-32 h-32 rounded-full object-cover border-4"
                style={{ borderColor: theme?.primaryHex }}
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{doctor.name}</h2>
                <p className="text-gray-600 mb-2">{doctor.title}</p>
                <p className="text-gray-500 text-sm mb-3">{doctor.credentials}</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" style={{ color: theme?.primaryHex }} />{doctor.hospitalFull}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" style={{ color: theme?.primaryHex }} />{doctor.timings}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Symptom-Based Navigation */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">What Type of Shoulder Pain Do You Have?</h2>
            <p className="text-gray-600 text-center mb-10">Click on your symptom to find the right condition and treatment options</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {symptomCategories.map((category, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>
                        {category.icon}
                      </div>
                      <CardTitle className="text-lg">{category.symptom}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                    <div className="space-y-2">
                      {category.conditions.map((condition, idx) => (
                        <Link 
                          key={idx}
                          to={condition.link}
                          className="flex items-center justify-between text-sm py-2 px-3 rounded hover:bg-gray-100 transition-colors"
                        >
                          <span>{condition.name}</span>
                          <ArrowRight className="w-4 h-4" style={{ color: theme?.primaryHex }} />
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Dr. Reddy for Shoulder Treatment?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme?.primaryHex}15` }}>
                  <span className="text-2xl font-bold" style={{ color: theme?.primaryHex }}>30+</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Years of Experience</h3>
                  <p className="text-gray-600 text-sm">Trained in UK, Mayo Clinic & Florida. FRCS certified.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme?.primaryHex}15` }}>
                  <span className="text-2xl font-bold" style={{ color: theme?.primaryHex }}>15K+</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Patients Treated</h3>
                  <p className="text-gray-600 text-sm">Thousands of successful shoulder treatments.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme?.primaryHex}15` }}>
                  <span className="text-xl font-bold" style={{ color: theme?.primaryHex }}>70%</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Non-Surgical Success</h3>
                  <p className="text-gray-600 text-sm">Most shoulder conditions improve without surgery.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${theme?.primaryHex}15` }}>
                  <span className="text-xl font-bold" style={{ color: theme?.primaryHex }}>5.0</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Patient Rating</h3>
                  <p className="text-gray-600 text-sm">250+ reviews on Practo and Justdial.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Conditions Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">All Shoulder Conditions We Treat</h2>
            <p className="text-gray-600 text-center mb-10">Comprehensive care for every shoulder problem</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allConditions.map((condition, index) => (
                <Link 
                  key={index}
                  to={condition.link}
                  className={`p-4 rounded-lg text-center hover:shadow-md transition-all ${
                    condition.priority === 'high' ? 'bg-orange-50 border border-orange-200' : 'bg-white border border-gray-200'
                  }`}
                >
                  <span className="text-gray-800 font-medium">{condition.name}</span>
                  <ArrowRight className="w-4 h-4 mx-auto mt-2" style={{ color: theme?.primaryHex }} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Visit Our Clinic in Hyderabad</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Apollo Hospitals Financial District</h3>
                <p className="text-gray-600 mb-4">{doctor.address}</p>
                <p className="text-gray-600 mb-2"><strong>Consultation Hours:</strong> {doctor.timings}</p>
                <p className="text-gray-600 mb-4"><strong>Phone:</strong> {doctor.phone}</p>
                <a 
                  href={doctor.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: theme?.primaryHex }}
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions on Google Maps
                </a>
              </div>
              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Book Your Appointment</h3>
                <p className="text-gray-600 mb-4">
                  Get expert evaluation for your shoulder pain. Most patients are seen within 24-48 hours.
                </p>
                <div className="space-y-3">
                  <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer" className="block">
                    <button 
                      className="w-full py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: theme?.primaryHex }}
                    >
                      Book Online at Apollo
                    </button>
                  </a>
                  <a href={`tel:${doctor.phone}`} className="block">
                    <button className="w-full py-3 rounded-lg border-2 font-medium hover:bg-white transition-colors" style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}>
                      Call {doctor.phone}
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default ShoulderPainTreatmentHyderabad;
